import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const syncInstructorToContacts = async (
  brandId: string,
  instructor: { name: string; email?: string | null }
): Promise<string | null> => {
  if (!instructor.email) return null;
  
  const nameParts = (instructor.name || '').trim().split(/\s+/);
  const firstName = nameParts[0] || instructor.email.split('@')[0];
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  
  try {
    const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
      p_brand_id: brandId,
      p_email: instructor.email,
      p_first_name: firstName,
      p_last_name: lastName,
      p_phone: null,
      p_module: ['lms'],
      p_activity: ['instructor'],
      p_organization: null,
      p_title: null,
      p_user_id: null,
    });
    
    if (error) {
      console.error('Error syncing instructor to contacts:', error);
      return null;
    }
    
    return contactId;
  } catch (error) {
    console.error('Error syncing instructor to contacts:', error);
    return null;
  }
};

export interface Instructor {
  id: string;
  brand_id: string;
  name: string;
  title?: string | null;
  bio?: string | null;
  email?: string | null;
  image_url?: string | null;
  social_links?: {
    linkedin?: string;
    twitter?: string;
    website?: string;
    github?: string;
  } | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useAdminInstructors(brandId: string | null) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: instructors, isLoading } = useQuery({
    queryKey: ['admin-instructors', brandId],
    queryFn: async () => {
      if (!brandId) return [];

      const { data, error } = await supabase
        .from('os_lms_instructors')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as Instructor[];
    },
    enabled: !!brandId,
  });

  const createInstructor = useMutation({
    mutationFn: async (instructor: Omit<Instructor, 'id' | 'created_at' | 'updated_at'>) => {
      // Sync to contacts first
      const contactId = await syncInstructorToContacts(instructor.brand_id, {
        name: instructor.name,
        email: instructor.email,
      });

      const { data, error } = await supabase
        .from('os_lms_instructors')
        .insert([{ ...instructor, contact_id: contactId }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-instructors', brandId] });
      toast({ title: 'Instructor created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create instructor', description: error.message, variant: 'destructive' });
    },
  });

  const updateInstructor = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Instructor> & { id: string }) => {
      // Get current instructor for contact sync
      const { data: currentInstructor } = await supabase
        .from('os_lms_instructors')
        .select('brand_id, name, email')
        .eq('id', id)
        .single();

      let contactId: string | null = null;
      if (currentInstructor) {
        contactId = await syncInstructorToContacts(currentInstructor.brand_id, {
          name: updates.name ?? currentInstructor.name,
          email: updates.email ?? currentInstructor.email,
        });
      }

      const { data, error } = await supabase
        .from('os_lms_instructors')
        .update({ ...updates, ...(contactId ? { contact_id: contactId } : {}) })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-instructors', brandId] });
      toast({ title: 'Instructor updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update instructor', description: error.message, variant: 'destructive' });
    },
  });

  const deleteInstructor = useMutation({
    mutationFn: async (instructorId: string) => {
      const { error } = await supabase
        .from('os_lms_instructors')
        .update({ is_active: false })
        .eq('id', instructorId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-instructors', brandId] });
      toast({ title: 'Instructor deactivated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to deactivate instructor', description: error.message, variant: 'destructive' });
    },
  });

  return {
    instructors: instructors || [],
    isLoading,
    createInstructor,
    updateInstructor,
    deleteInstructor,
  };
}

export function useInstructor(instructorId: string | null) {
  return useQuery({
    queryKey: ['instructor', instructorId],
    queryFn: async () => {
      if (!instructorId) return null;

      const { data, error } = await supabase
        .from('os_lms_instructors')
        .select('*')
        .eq('id', instructorId)
        .single();

      if (error) throw error;
      return data as Instructor;
    },
    enabled: !!instructorId,
  });
}
